import { useState, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
// Forms live outside react-admin's <Edit>, so useUpdate({ undoable }) is
// unusable and we dispatch CRUD_UPDATE_OPTIMISTIC directly.
import {
  CRUD_UPDATE,
  UPDATE,
  refreshView,
  startOptimisticMode,
  stopOptimisticMode,
} from 'react-admin';
import { createContainer } from 'unstated-next';

export type SnackbarSeverity = 'success' | 'error' | 'warning' | 'info';

// `data` is shallow-merged into the redux cache for `resource[id]`; nested
// relations overwrite rather than deep-merge.
export interface OptimisticUpdate {
  resource: string;
  id: string | number;
  data: object;
}

export interface UndoableOptions {
  message: string;
  onCommit: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
  severity?: SnackbarSeverity;
  duration?: number;
  // Mirrors react-admin's undoable mutation: updates the redux cache
  // immediately so the list view reflects values before the deferred request.
  optimistic?: OptimisticUpdate;
}

const DEFAULT_DURATION = 4000;

interface SnackbarState {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
  duration: number;
  undoable: boolean;
}

const INITIAL_STATE: SnackbarState = {
  open: false,
  message: '',
  severity: 'info',
  duration: DEFAULT_DURATION,
  undoable: false,
};

type PendingCallback = () => void | Promise<void>;

const runSafely = (fn: PendingCallback | null) => {
  if (!fn) return;
  Promise.resolve()
    .then(fn)
    .catch(err => console.error('Snackbar pending callback failed:', err));
};

function useSnackbar() {
  const dispatch = useDispatch();
  const [snackbar, setSnackbar] = useState<SnackbarState>(INITIAL_STATE);
  const pendingCommitRef = useRef<PendingCallback | null>(null);
  const pendingCancelRef = useRef<PendingCallback | null>(null);
  const optimisticActiveRef = useRef(false);

  const consumePending = useCallback(() => {
    const commit = pendingCommitRef.current;
    const cancel = pendingCancelRef.current;
    pendingCommitRef.current = null;
    pendingCancelRef.current = null;
    return { commit, cancel };
  }, []);

  const startOptimistic = useCallback(
    ({ resource, id, data }: OptimisticUpdate) => {
      optimisticActiveRef.current = true;
      dispatch(startOptimisticMode());
      // Shape required by react-admin's resource data reducer to merge `data`
      // into state.admin.resources[resource].data[id] synchronously.
      dispatch({
        type: `${CRUD_UPDATE}_OPTIMISTIC`,
        payload: { id, data },
        meta: { resource, fetch: UPDATE, optimistic: true },
      });
    },
    [dispatch],
  );

  const stopOptimisticIfActive = useCallback(
    (shouldRefresh: boolean) => {
      if (!optimisticActiveRef.current) return;
      optimisticActiveRef.current = false;
      dispatch(stopOptimisticMode());
      if (shouldRefresh) dispatch(refreshView());
    },
    [dispatch],
  );

  const notify = useCallback(
    (message: string, severity: SnackbarSeverity = 'info') => {
      // Don't touch pending refs or optimistic mode: any active undoable's
      // commit still fires via the next onExited, so data isn't dropped.
      setSnackbar({
        open: true,
        message,
        severity,
        duration: DEFAULT_DURATION,
        undoable: false,
      });
    },
    [],
  );

  const notifyWithUndo = useCallback(
    ({
      message,
      onCommit,
      onCancel,
      severity = 'info',
      duration = DEFAULT_DURATION,
      optimistic,
    }: UndoableOptions) => {
      const prevCommit = pendingCommitRef.current;
      pendingCommitRef.current = onCommit;
      pendingCancelRef.current = onCancel ?? null;
      // Settle any prior undoable first so its request fires before stacking.
      stopOptimisticIfActive(false);
      runSafely(prevCommit);
      if (optimistic) startOptimistic(optimistic);
      setSnackbar({
        open: true,
        message,
        severity,
        duration,
        undoable: true,
      });
    },
    [startOptimistic, stopOptimisticIfActive],
  );

  const dismissSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const flushPendingCommit = useCallback(() => {
    const { commit } = consumePending();
    stopOptimisticIfActive(false);
    runSafely(commit);
  }, [consumePending, stopOptimisticIfActive]);

  const cancelUndoable = useCallback(() => {
    const { cancel } = consumePending();
    setSnackbar(prev => ({ ...prev, open: false }));
    stopOptimisticIfActive(true);
    runSafely(cancel);
  }, [consumePending, stopOptimisticIfActive]);

  return {
    snackbar,
    notify,
    notifyWithUndo,
    dismissSnackbar,
    flushPendingCommit,
    cancelUndoable,
  };
}

export const SnackbarContainer = createContainer(useSnackbar);
